import { useLang } from '@/app/utils/useLang';
import { referalUrl, referraledUrl } from '@/components/Ambassador/constants';
import { useReferralDispatch, useReferralStore } from '@/components/Ambassador/ReferralProvider';
import { copyText } from '@/components/Ambassador/util';
import Button from '@/components/UI/Button';
import { message } from '@/components/UI/Message';
import EfModal from '@/components/UI/Modal';
import { sendInviteEmail } from '@/service/referral';
import { getIsLogin, loginUser } from '@/utils/cookie';
import { signupCookie } from '@/utils/cookies/signupCookie';
import efBridge from '@/utils/JSBridge';
import { useGlobal } from '@/utils/useGlobal';
import EfLink from '@elem/ui/ef-link';
import { StatGetDeviceId } from 'eco-design-micro/EcoStat/config';
import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import styled from './index.module.scss';

export type ReferHandle = {
    show: () => void;
};
interface ReferPorps {
    isShare?: boolean;
}
enum TAB_TYPE {
    Referrer,
    Referee,
}
const reg = /^[A-Za-z\d]+([.%~`$^*&(){}<>?"+=!_\\-]*[A-Za-z\d-])*@[a-zA-Z\d-]{1,30}\.[a-zA-Z\d.%#~`$^*&(){}<>?"+=!_\\-]{1,30}$/;

const ICON_FB = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
        <g clipPath="url(#clip0_1493_105443)">
            <path
                d="M30 15C30 6.71572 23.2843 0 15 0C6.71572 0 0 6.71572 0 15C0 22.4868 5.48525 28.6925 12.6562 29.8178V19.3359H8.84766V15H12.6562V11.6953C12.6562 7.93594 14.8957 5.85938 18.322 5.85938C19.9626 5.85938 21.6797 6.15234 21.6797 6.15234V9.84375H19.7883C17.925 9.84375 17.3438 11.0001 17.3438 12.1875V15H21.5039L20.8389 19.3359H17.3438V29.8178C24.5147 28.6925 30 22.4868 30 15Z"
                fill="#1877F2"
            />
            <path
                d="M20.8389 19.3359L21.5039 15H17.3438V12.1875C17.3438 11.0013 17.925 9.84375 19.7883 9.84375H21.6797V6.15234C21.6797 6.15234 19.9632 5.85938 18.322 5.85938C14.8957 5.85938 12.6562 7.93594 12.6562 11.6953V15H8.84766V19.3359H12.6562V29.8178C14.2093 30.0607 15.7907 30.0607 17.3438 29.8178V19.3359H20.8389Z"
                fill="white"
            />
        </g>
        <defs>
            <clipPath id="clip0_1493_105443">
                <rect width="30" height="30" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

const ICON_X = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.2849 16.3141L0.570135 28.5443H5.31663L13.518 19.1581L20.7447 28.5463L30 28.4958L18.2657 12.9788L28.2795 1.50828L23.6092 1.45605L16.0451 10.0765L9.57357 1.47013L0 1.45974L11.2849 16.3141ZM24.3947 25.7291L21.9995 25.7216L5.5435 4.17791H8.11994L24.3947 25.7291Z"
            fill="black"
        />
    </svg>
);

const Refer = ({ isShare = true }: ReferPorps, ref: ForwardedRef<ReferHandle>) => {
    const { useRouter, isApp } = useGlobal();
    const router = useRouter();
    const { locale, asPath } = useRouter();
    const { t } = useLang('referral');
    const { t: sharedT } = useLang('shared');
    const [show, setShow] = useState(false);
    const { shareInfo, referralInfo } = useReferralStore();
    const dispatch = useReferralDispatch();
    const [copyValue, setCopyValue] = useState('');
    const [sendEmail, setSendEmail] = useState('');
    const isLogin = getIsLogin();

    const referralPath = referalUrl[locale] || referalUrl.us;
    const sharePath = referraledUrl[locale] || referraledUrl.us;
    // 分享文案
    const shareText = {
        faceBook: `${t('refer.share_text')}\n${copyValue}`,
        twitter: `${t('refer.share_text')}\n${copyValue}`,
    };

    const handleShareInfo = () => {
        efBridge.callHandler('shareMessage', {
            title: t('refer.share_text'),
            url: copyValue,
        });
    };

    const handleShare = async () => {
        if (!isLogin) {
            signupCookie?.({
                sector_name: t('refer.pop_refer_title'),
                entry_position: 4,
            });
            location.href = `/${locale}/login?redirect=${encodeURIComponent(`/${locale}/${asPath}?pop=share`)}`;
            return;
        }
        if (!isShare) {
            router.push(referralPath);
            return;
        }
        // const fp = await FingerprintJS.load();
        // const { visitorId } = await fp.get();
        const visitorId = await StatGetDeviceId({});
        dispatch({
            type: 'getShareInfo',
            payload: {
                data: {
                    planId: referralInfo?.planId,
                    shareUrl: `${location.origin}/${locale}/${sharePath}`,
                },
                deviceId: visitorId,
                locale,
            },
        });
        setShow(true);
    };

    const handleSendMsg = () => {
        if (!referralInfo || !shareInfo) return;
        const { email } = loginUser().user;
        // 如果与用户邮箱相同提示
        if (email && email === sendEmail.trim()) return message.error(t('refer.thesameemail'));
        sendInviteEmail(
            {
                planId: referralInfo?.planId,
                shareEmail: sendEmail.trim(),
                shareId: shareInfo?.shareId,
                shareUrl: copyValue,
            },
            locale,
        )
            .then((res) => {
                if (res.code !== '0') return message.error(res.message);
                message.success(sharedT('my.site_account_profile_save_success'));
                setSendEmail('');
            })
            .catch((err) => {
                console.log(err);
            });
    };

    useEffect(() => {
        if (!shareInfo) return;
        setCopyValue(`${location.origin}/${locale}/${sharePath}?inviteCode=${shareInfo?.inviteCode}`);
    }, [shareInfo]);

    useImperativeHandle(ref, () => ({
        show: () => handleShare(),
    }));

    if (!referralInfo) return <></>;

    return (
        <div className={styled.refer}>
            <EfModal className={`${styled.modal}`} visible={show} open={show} width={450} onCancel={() => setShow(false)}>
                <div className="content">
                    <h1 className="title">{t('refer.pop_refer_title')}</h1>
                    <p className="desc">{t('refer.pop_refer_desc')}</p>
                    <div className="email-content">
                        <p className="sub-title">{t('refer.pop_share_link_title')}</p>
                        <div className="input-btn">
                            <input type="text" value={copyValue} disabled placeholder={t('modal.placeholder')} />
                            <button onClick={() => copyText(copyValue, sharedT, locale)}>{t('refer.pop_share_link_btn')}</button>
                        </div>
                        <div className="line">
                            <p>{t('refer.pop_other')}</p>
                        </div>
                    </div>
                    <div className="email-content">
                        <p className="sub-title">{t('refer.pop_email_directly_title')}</p>
                        <div className="input-btn">
                            <input type="text" placeholder={t('refer.pop_input_placeholder')} onChange={(e) => setSendEmail(e.target.value)} />
                            <button onClick={handleSendMsg} disabled={!reg.test(sendEmail)}>
                                {t('refer.pop_email_directly_btn')}
                            </button>
                        </div>
                        <div className="line">
                            <p>{t('refer.pop_other')}</p>
                        </div>
                    </div>
                    <div className="icons">
                        {isApp ? (
                            <Button className="refer_modal_btn" onClick={handleShareInfo}>
                                {t('refer.pop_share_conmon_share')}
                            </Button>
                        ) : (
                            <>
                                <EfLink href={`https://www.facebook.com/sharer/sharer.php?u=${copyValue}`} target="_blank">
                                    <ICON_FB />
                                </EfLink>
                                <EfLink href={`https://twitter.com/intent/tweet?text=${shareText.twitter}`} target="_blank">
                                    <ICON_X />
                                </EfLink>
                            </>
                        )}
                    </div>
                    <p className="tips">{t('refer.share_footer_info')}</p>
                </div>
            </EfModal>
        </div>
    );
};

export default forwardRef<ReferHandle, ReferPorps>(Refer);
